import { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import ScrollReveal from '@/components/base/ScrollReveal';
import JsonLd from '@/components/base/JsonLd';
import { useTranslation } from 'react-i18next';

const solutionIds = ['tours-pylones', 'fibre-optique', 'maintenance-telecom', 'micro-reseaux', 'pompage', 'stockage', 'recharge-ve', 'residentiel', 'industriel'];

const solutionMeta: Record<string, { id: string; icon: string; image: string }> = {
  'tours-pylones': { id: 'tours-pylones', icon: 'ri-radio-line', image: 'https://readdy.ai/api/search-image?query=Black%20African%20telecom%20engineers%20with%20dark%20skin%20working%20on%20steel%20lattice%20tower%20installation%20in%20African%20landscape%2C%20professional%20dark-skinned%20technicians%20deploying%20cellular%20antennas%20and%20microwave%20equipment%2C%20bright%20blue%20sky%20with%20clouds%2C%20modern%20telecom%20infrastructure%20deployment%2C%20professional%20industrial%20photography%20with%20warm%20natural%20lighting&width=800&height=500&seq=tel-towers-sol-01&orientation=landscape&nocache=true' },
  'fibre-optique': { id: 'fibre-optique', icon: 'ri-route-line', image: 'https://readdy.ai/api/search-image?query=Black%20African%20fiber%20optic%20technicians%20with%20dark%20skin%20splicing%20optical%20fibers%20with%20specialized%20equipment%20in%20urban%20African%20setting%2C%20professional%20dark-skinned%20engineers%20deploying%20fiber%20optic%20cable%20network%2C%20modern%20telecommunications%20infrastructure%2C%20clean%20professional%20scene%20with%20focused%20lighting&width=800&height=500&seq=tel-fibre-sol-01&orientation=landscape&nocache=true' },
  'maintenance-telecom': { id: 'maintenance-telecom', icon: 'ri-tools-line', image: 'https://readdy.ai/api/search-image?query=Black%20African%20telecom%20maintenance%20engineers%20with%20dark%20skin%20working%20on%20radio%20equipment%20and%20network%20hardware%20at%20base%20station%2C%20professional%20dark-skinned%20technical%20team%20with%20safety%20gear%20performing%20preventive%20maintenance%2C%20modern%20telecommunications%20facility%2C%20clean%20industrial%20setting%20with%20organized%20equipment%20racks&width=800&height=500&seq=tel-maint-sol-01&orientation=landscape&nocache=true' },
  'micro-reseaux': { id: 'micro-reseaux', icon: 'ri-flashlight-line', image: 'https://readdy.ai/api/search-image?query=Modern%20solar%20microgrid%20installation%20in%20African%20village%20with%20battery%20storage%20containers%20and%20solar%20panels%2C%20rural%20electrification%20project%20with%20smart%20energy%20management%20system%2C%20professional%20renewable%20energy%20infrastructure%2C%20bright%20sunny%20day&width=800&height=500&seq=sol-microgrid-01&orientation=landscape' },
  'pompage': { id: 'pompage', icon: 'ri-drop-line', image: 'https://readdy.ai/api/search-image?query=Solar%20water%20pumping%20system%20in%20rural%20agricultural%20setting%20with%20green%20fields%2C%20modern%20solar%20panels%20powering%20irrigation%20pump%2C%20professional%20renewable%20energy%20agriculture%20infrastructure%2C%20bright%20sunny%20day%2C%20sustainable%20farming%20technology&width=800&height=500&seq=sol-pumping-01&orientation=landscape' },
  'stockage': { id: 'stockage', icon: 'ri-battery-charge-line', image: 'https://storage.helloreaddy.io/project_files/dd4db55e-adff-421a-ab07-f57f9542cdee/a85baacd-21b0-4f75-86e9-af0bab62b940_compressed_WhatsApp-Image-2026-05-01-at-12.05.45.webp' },
  'recharge-ve': { id: 'recharge-ve', icon: 'ri-car-line', image: 'https://readdy.ai/api/search-image?query=Electric%20vehicle%20charging%20station%20powered%20by%20solar%20panels%20canopy%2C%20modern%20EV%20charging%20point%20with%20green%20energy%20infrastructure%2C%20sustainable%20transportation%20technology%2C%20clean%20bright%20day%2C%20futuristic%20renewable%20energy%20mobility%20solution&width=800&height=500&seq=sol-ev-01&orientation=landscape' },
  'residentiel': { id: 'residentiel', icon: 'ri-home-line', image: 'https://readdy.ai/api/search-image?query=Residential%20solar%20panel%20installation%20on%20modern%20house%20roof%20with%20blue%20sky%2C%20suburban%20home%20with%20photovoltaic%20system%20and%20battery%20storage%20unit%2C%20clean%20energy%20family%20house%2C%20professional%20sustainable%20home%20energy%20solution%20photography&width=800&height=500&seq=sol-residential-01&orientation=landscape' },
  'industriel': { id: 'industriel', icon: 'ri-building-line', image: 'https://storage.helloreaddy.io/project_files/dd4db55e-adff-421a-ab07-f57f9542cdee/06481eaf-5fd9-4dc9-8fa9-36d9b107acc3_compressed_WhatsApp-Image-2026-05-01-at-16.42.33.webp' },
};

export default function Solutions() {
  const { t } = useTranslation();
  const [activeSolution, setActiveSolution] = useState(solutionIds[0]);

  const currentMeta = solutionMeta[activeSolution];
  const solIndex = solutionIds.indexOf(activeSolution);
  const currentSol = (t('solutions.solutions', { returnObjects: true }) as Array<Record<string, unknown>>)?.[solIndex] as Record<string, unknown> | undefined;

  return (
    <Layout>
      <JsonLd data={{
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Solutions et Services IS TELECOM',
        description: 'Infrastructures de télécommunication et solutions énergétiques sur mesure : tours et pylônes, fibre optique, maintenance de télécommunication, micro-réseaux, pompage solaire, stockage d\'énergie et plus.',
        url: 'https://is-telecom.co/solutions',
        itemListElement: [
          { '@type': 'Service', position: 1, name: 'Tours et Pylônes Télécoms', description: 'Installation et maintenance de tours de télécommunication, pylônes autostables et haubanés.', provider: { '@type': 'Organization', name: 'IS TELECOM' } },
          { '@type': 'Service', position: 2, name: 'Réseaux Fibre Optique', description: 'Déploiement de réseaux fibre optique et infrastructure de transmission pour opérateurs de télécommunication.', provider: { '@type': 'Organization', name: 'IS TELECOM' } },
          { '@type': 'Service', position: 3, name: 'Maintenance & Services Télécoms', description: 'Maintenance préventive et curative, support technique 24/7 pour infrastructures de télécommunication.', provider: { '@type': 'Organization', name: 'IS TELECOM' } },
          { '@type': 'Service', position: 4, name: 'Systèmes de Micro-réseaux', description: 'Solutions complètes pour l\'électrification rurale et urbaine avec gestion intelligente.', provider: { '@type': 'Organization', name: 'IS TELECOM' } },
          { '@type': 'Service', position: 5, name: 'Pompage Solaire', description: 'Systèmes de pompage et d\'irrigation solaire pour l\'agriculture et l\'approvisionnement en eau.', provider: { '@type': 'Organization', name: 'IS TELECOM' } },
          { '@type': 'Service', position: 6, name: 'Stockage d\'Énergie', description: 'Solutions de stockage domestique et industrielle avec batteries lithium haute performance.', provider: { '@type': 'Organization', name: 'IS TELECOM' } },
          { '@type': 'Service', position: 7, name: 'Recharge Véhicules Électriques', description: 'Bornes de recharge solaire pour véhicules électriques, solutions complètes et durables.', provider: { '@type': 'Organization', name: 'IS TELECOM' } },
          { '@type': 'Service', position: 8, name: 'Solutions Résidentielles', description: 'Systèmes solaires pour maisons, hôtels et établissements de santé.', provider: { '@type': 'Organization', name: 'IS TELECOM' } },
          { '@type': 'Service', position: 9, name: 'Solutions Industrielles', description: 'Systèmes haute puissance pour zones rurales, exploitations minières et industries.', provider: { '@type': 'Organization', name: 'IS TELECOM' } },
        ],
      }} />
      {/* Hero */}
      <section className="relative py-20 bg-background-950 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20dark%20telecommunications%20network%20pattern%20with%20glowing%20fiber%20optic%20lines%20and%20cellular%20tower%20silhouettes%2C%20modern%20tech%20background%20for%20telecom%20company%2C%20dark%20navy%20background%20with%20teal%20and%20green%20light%20accents%2C%20professional%20corporate%20design%20with%20circuit-like%20connections&width=1920&height=700&seq=sol-hero-tel-01&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-16">
          <h1 className="text-4xl md:text-6xl font-bold font-heading text-white mb-4">
            {t('solutions.pageTitle')}
          </h1>
          <p className="text-lg md:text-xl text-background-300 max-w-3xl mx-auto">
            {t('solutions.pageSubtitle')}
          </p>
        </div>
      </section>

      {/* Solutions Navigation + Detail */}
      <ScrollReveal variant="fade-up">
      <section className="py-24 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar */}
            <div className="lg:w-1/3 flex lg:flex-col gap-3 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
              {solutionIds.map((id, idx) => {
                const sol = (t('solutions.solutions', { returnObjects: true }) as Array<Record<string, unknown>>)?.[idx] as Record<string, unknown> | undefined;
                const meta = solutionMeta[id];
                if (!sol || !meta) return null;
                return (
                  <button
                    key={id}
                    onClick={() => setActiveSolution(id)}
                    className={`flex-shrink-0 flex items-center gap-4 px-5 py-4 rounded-2xl text-left transition-all cursor-pointer whitespace-nowrap border ${
                      activeSolution === id
                        ? 'bg-background-950 text-white border-background-950 shadow-md'
                        : 'bg-white text-foreground-700 hover:bg-background-100 border-background-200/70'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      activeSolution === id ? 'bg-primary-500' : 'bg-primary-100'
                    }`}>
                      <i className={`${meta.icon} ${activeSolution === id ? 'text-background-950' : 'text-primary-600'} text-lg w-5 h-5 flex items-center justify-center`}></i>
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm">{String(sol.title || '')}</div>
                      <div className={`text-xs truncate ${activeSolution === id ? 'text-background-400' : 'text-foreground-400'}`}>
                        {String(sol.shortDesc || '').slice(0, 40)}...
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Detail panel */}
            <div className="lg:w-2/3">
              <div className="bg-white rounded-3xl overflow-hidden border border-background-200/70">
                <div className="h-56 md:h-72 overflow-hidden">
                  <img src={currentMeta.image} alt={String(currentSol?.title || '')} className="w-full h-full object-cover" />
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                      <i className={`${currentMeta.icon} text-primary-600 text-xl w-6 h-6 flex items-center justify-center`}></i>
                    </div>
                    <h2 className="text-2xl font-bold font-heading text-foreground-950">{String(currentSol?.title || '')}</h2>
                  </div>
                  <p className="text-foreground-500 leading-relaxed mb-6">{String(currentSol?.fullDesc || '')}</p>

                  <div className="grid md:grid-cols-2 gap-8 mb-6">
                    <div>
                      <h3 className="font-bold text-foreground-950 mb-3 flex items-center gap-2 text-sm">
                        <i className="ri-check-double-line text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                        {t('solutions.keyFeatures')}
                      </h3>
                      <ul className="space-y-2">
                        {(currentSol?.features as string[] || []).map((f: string, i: number) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-foreground-500">
                            <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-bold text-foreground-950 mb-3 flex items-center gap-2 text-sm">
                        <i className="ri-map-pin-line text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                        {t('solutions.useCases')}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {(currentSol?.useCases as string[] || []).map((uc: string, i: number) => (
                          <span key={i} className="bg-primary-50 text-primary-700 px-3 py-1.5 rounded-full text-xs font-medium border border-primary-100">
                            {uc}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <Link
                    to="/devis"
                    className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-950 px-6 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-file-text-line w-5 h-5 flex items-center justify-center"></i>
                    {t('solutions.requestQuote')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* Process */}
      <ScrollReveal variant="fade-up" delay={100}>
      <section className="py-24 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center border border-primary-300/60 rounded-full px-4 py-1.5 mb-6">
              <span className="text-primary-600 text-xs font-semibold uppercase tracking-wider">{t('solutions.processBadge')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold font-heading text-foreground-950">
              {t('solutions.processTitle')}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {(() => {
              const steps = t('solutions.processSteps', { returnObjects: true }) as Array<Record<string, string>>;
              const icons = ['ri-search-line', 'ri-pencil-ruler-line', 'ri-tools-line', 'ri-customer-service-2-line'];
              return steps.map((s, i) => (
                <div key={i} className="text-center">
                  <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-5 relative">
                    <i className={`${icons[i]} text-primary-600 text-2xl w-8 h-8 flex items-center justify-center`}></i>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-primary-500 text-background-950 rounded-full flex items-center justify-center text-sm font-bold">
                      {String(i + 1).padStart(2, '0')}
                    </div>
                  </div>
                  <h3 className="font-bold font-heading text-foreground-950 text-lg mb-2">{s.title}</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">{s.desc}</p>
                </div>
              ));
            })()}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* CTA */}
      <ScrollReveal variant="fade-up" delay={100}>
      <section className="py-24 bg-background-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold font-heading text-white mb-4">{t('solutions.ctaTitle')}</h2>
          <p className="text-lg text-background-300 max-w-3xl mx-auto mb-8">
            {t('solutions.ctaSubtitle')}
          </p>
          <Link to="/devis" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-950 px-8 py-4 rounded-full text-lg font-semibold transition-all cursor-pointer whitespace-nowrap">
            {t('solutions.ctaButton')}
            <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center"></i>
          </Link>
        </div>
      </section>
      </ScrollReveal>
    </Layout>
  );
}